import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Solicitacao } from '@/types/solicitacao';
import { useClientFilter } from './useClientFilter';
import { transformIncidenteData } from '@/utils/incidenteDataTransform';

export const useIncidentesTechnician = () => {
  const { 
    currentClientId, 
    isAdmin, 
    isTechnician, 
    clientLoading
  } = useClientFilter();
  
  const { data: incidentes = [], isLoading, error } = useQuery({
    queryKey: ['incidentes-technician', currentClientId, isAdmin, isTechnician],
    queryFn: async () => {
      let query = supabase
        .from('incidentes')
        .select('*');

      const { data, error } = await query.order('criado_em', { ascending: false });

      if (error) {
        throw error;
      }
      
      if (!data || data.length === 0) {
        return [];
      }

      // Extrair IDs únicos para buscar relacionamentos
      const userIds = [...new Set([
        ...data.filter(i => i.solicitante_id).map(i => i.solicitante_id),
        ...data.filter(i => i.atendente_id).map(i => i.atendente_id)
      ])];

      const clientIds = [...new Set(data.filter(i => i.client_id).map(i => i.client_id))];
      const groupIds = [...new Set(data.filter(i => i.grupo_responsavel_id).map(i => i.grupo_responsavel_id))];
      const categoryIds = [...new Set(data.filter(i => i.categoria_id).map(i => i.categoria_id))];
      const slaIds = [...new Set(data.filter(i => i.sla_id).map(i => i.sla_id))];

      // Buscar dados relacionados em paralelo
      const [usersData, clientsData, groupsData, categoriesData, slasData] = await Promise.all([
        userIds.length > 0 ? supabase.from('users').select('id, name').in('id', userIds) : Promise.resolve({ data: [] }),
        clientIds.length > 0 ? supabase.from('clients').select('id, name').in('id', clientIds) : Promise.resolve({ data: [] }),
        groupIds.length > 0 ? supabase.from('groups').select('id, name, email').in('id', groupIds) : Promise.resolve({ data: [] }),
        categoryIds.length > 0 ? supabase.from('categorias_servico').select('id, nome').in('id', categoryIds) : Promise.resolve({ data: [] }),
        slaIds.length > 0 ? supabase.from('slas').select('id, nome').in('id', slaIds) : Promise.resolve({ data: [] })
      ]);

      // Transformar dados com relacionamentos
      const usersMap = new Map((usersData.data || []).map(user => [user.id, user]));
      const clientsMap = new Map((clientsData.data || []).map(client => [client.id, client]));
      const groupsMap = new Map((groupsData.data || []).map(group => [group.id, group]));
      const categoriesMap = new Map((categoriesData.data || []).map(cat => [cat.id, cat]));
      const slasMap = new Map((slasData.data || []).map(sla => [sla.id, sla]));

      return transformIncidenteData(data, {
        usersMap,
        clientsMap,
        groupsMap,
        categoriesMap,
        slasMap
      });
    },
    enabled: !clientLoading,
  });

  return {
    incidentes,
    isLoading,
    error,
  };
};